import Loading from "@/components/loading/loading.vue";
import { ImageData } from "@/models/image-meta-data";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { cloneDeep } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ImageEditorFlipAxis, ImageEditorFlipEvent, ImageEditorRotateEvent } from ".";

export enum ImageEditorActions {
	Move,
	Crop,
	ZoomIn,
	ZoomOut,
	RotateLeft,
	RotateRight,
	FlipHorizontal,
	FlipVertical
}

@Component({
	components: {
		Loading
	}
})
export default class ImageEditor extends Vue {
	ImageEditorActions = ImageEditorActions;

	@Prop()
	public src!: string;

	@Prop({
		default: () => ({})
	})
	public readonly placeholder!: { icon?: string; text?: string };

	@Prop()
	public mimeType!: string;

	@Prop()
	public disabled?: boolean;

	private cropperData: Cropper.Data | null = null;
	public data: ImageData = {
		loading: false,
		name: "",
		type: "",
		url: ""
	};

	private cropper: Cropper = null as any;
	private isFocused: boolean = false;

	$refs!: {
		image: HTMLImageElement;
		wrapper: HTMLDivElement;
	};

	created() {
		this.update({ url: this.src || "", type: this.mimeType, loading: !!this.src });
	}

	@Watch("src")
	public srcUpdate() {
		this.stop();
		this.update({ url: this.src, type: this.mimeType, loading: !!this.src });
	}

	@Watch("disabled")
	applyDisableCheck() {
		if (this.cropper) {
			if (this.disabled) {
				this.cropper.disable();
			} else {
				this.cropper.enable();
			}
		}
	}

	public mounted() {
		window.addEventListener("keydown", (this.keydown = this.keydown.bind(this)));
		this.handleFocus();
	}

	public beforeDestroy() {
		window.removeEventListener("keydown", this.keydown);
		this.stop();
	}

	public start() {
		if (this.cropper) {
			return;
		}
		this.cropper = new Cropper(this.$refs.image, {
			autoCrop: false,
			dragMode: "move",
			background: false,
			ready: () => {
				this.emitCanvasChange();
				this.onCropReady();
			},
			zoom: this.emitCanvasChange,
			cropmove: this.emitCanvasChange,
			viewMode: 2
		});
		this.applyDisableCheck();
	}

	private emitCanvasChange() {
		setTimeout(() => {
			this.$emit("canvasChange", this.cropper.getCanvasData());
		}, 10);
	}

	public applyCropperData(data: Cropper.Data) {
		this.cropperData = cloneDeep(data);
	}

	private onCropReady() {
		if (this.cropperData) {
			if (this.cropperData.rotate) {
				this.cropper.rotateTo(this.cropperData.rotate);
			}

			if (this.cropperData.width !== 0) {
				const { scaleX, scaleY, rotate, ...cropBoxOptions } = this.cropperData;
				this.cropper.crop();
				this.cropper.setData(cropBoxOptions);
			}

			// While editing apply the flip on correct axis, on the basis of rotation.
			// Same logic is applied in flip method, because of which the flip effect will be applied on correct axis.
			const isDimentionChanged = (Math.abs(this.cropperData.rotate) / 90) % 2 !== 0;

			if (this.cropperData.scaleX) {
				this.flip(
					isDimentionChanged ? ImageEditorFlipAxis.vertical : ImageEditorFlipAxis.horizontal,
					this.cropperData.scaleX,
					false
				);
			}

			if (this.cropperData.scaleY) {
				this.flip(
					isDimentionChanged ? ImageEditorFlipAxis.horizontal : ImageEditorFlipAxis.vertical,
					this.cropperData.scaleY,
					false
				);
			}
		}

		this.update({ loading: false });
		this.$emit("ready");
	}

	private isDataUrl(url: string): boolean {
		const parser = document.createElement("a");
		parser.href = url;
		const isDataUrl = parser.protocol === "data:";
		parser.remove();
		return isDataUrl;
	}

	private downloadImageAsBase64Url(url: string, mimeType: string): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = "Anonymous";
			img.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d")!;
				canvas.height = img.naturalHeight;
				canvas.width = img.naturalWidth;
				ctx.drawImage(img, 0, 0);
				const base64Url = canvas.toDataURL(mimeType);
				resolve(base64Url);
			};
			img.onerror = reject;
			img.src = url;
		});
	}

	public click(action: ImageEditorActions) {
		const { cropper } = this;
		switch (action) {
			case ImageEditorActions.Move:
				cropper.setDragMode("move");
				break;
			case ImageEditorActions.Crop:
				cropper.setDragMode("crop");
				break;
			case ImageEditorActions.ZoomIn:
				cropper.zoom(0.1);
				break;
			case ImageEditorActions.ZoomOut:
				cropper.zoom(-0.1);
				break;
			case ImageEditorActions.RotateLeft:
				this.rotate(false);
				break;
			case ImageEditorActions.RotateRight:
				this.rotate(true);
				break;
			case ImageEditorActions.FlipHorizontal:
				this.flip(ImageEditorFlipAxis.horizontal);
				break;
			case ImageEditorActions.FlipVertical:
				this.flip(ImageEditorFlipAxis.vertical);
				break;
			default:
		}
	}

	private handleFocus(): void {
		this.$refs.wrapper.onfocus = () => {
			this.isFocused = true;
		};
		this.$refs.wrapper.onblur = () => {
			this.isFocused = false;
		};
		this.$refs.wrapper.addEventListener("click", () => {
			this.$refs.wrapper.focus();
		});
	}

	private keydown(e: KeyboardEvent) {
		if (!this.isFocused) {
			return;
		}

		switch (e.key) {
			// Undo crop
			case "z":
				if (e.ctrlKey) {
					e.preventDefault();
					this.restore();
				}
				break;

			default:
		}
		const { cropper } = this;
		if (!cropper) {
			return;
		}
		switch (e.key) {
			// Crop the image
			case "Enter":
				this.complete();
				break;
			// Clear crop area
			case "Escape":
				e.preventDefault();
				this.clear();
				break;
			// Move to the left
			case "ArrowLeft":
				e.preventDefault();
				this.move(-1, 0);
				break;
			// Move to the top
			case "ArrowUp":
				e.preventDefault();
				this.move(0, -1);
				break;
			// Move to the right
			case "ArrowRight":
				e.preventDefault();
				this.move(1, 0);
				break;
			// Move to the bottom
			case "ArrowDown":
				e.preventDefault();
				this.move(0, 1);
				break;
			// Enter crop mode
			case "c":
				cropper.setDragMode("crop");
				break;
			// Enter move mode
			case "m":
				cropper.setDragMode("move");
				break;
			// Zoom in
			case "i":
				cropper.zoom(0.1);
				break;
			// Zoom out
			case "o":
				cropper.zoom(-0.1);
				break;
			// Rotate left
			case "l":
				this.rotate(false);
				break;
			// Rotate right
			case "r":
				this.rotate(true);
				break;
			// Flip horizontal
			case "h":
				this.flip(ImageEditorFlipAxis.horizontal);
				break;
			// Flip vertical
			case "v":
				this.flip(ImageEditorFlipAxis.vertical);
				break;
			default:
		}
	}

	private move(offsetX: number, offsetY?: number | undefined): Cropper {
		this.cropper.move(offsetX, offsetY);
		this.emitCanvasChange();
		return this.cropper;
	}
	private rotate(clockWise: boolean): Cropper {
		this.cropper.rotate(clockWise ? 90 : -90);

		// Emitting rotate event so annotation can get repositioned.
		const event: ImageEditorRotateEvent = { canvasData: this.cropper.getCanvasData(), clockWise };
		this.$emit("rotate", event);

		// Emitting canvas change so the annotation canvas can resize itself
		this.emitCanvasChange();

		return this.cropper;
	}

	private flip(axis: ImageEditorFlipAxis, value?: number, emitEvent: boolean = true): Cropper {
		const cropperData = this.cropper.getData();
		const isDimentionChanged = (Math.abs(cropperData.rotate) / 90) % 2 !== 0;
		let operatedAxis: ImageEditorFlipAxis = axis;
		if (axis === ImageEditorFlipAxis.horizontal && isDimentionChanged) {
			operatedAxis = ImageEditorFlipAxis.vertical;
		}
		if (axis === ImageEditorFlipAxis.vertical && isDimentionChanged) {
			operatedAxis = ImageEditorFlipAxis.horizontal;
		}

		if (!value) {
			value = -cropperData[operatedAxis] || -1;
		}

		this.cropper[operatedAxis](value);
		// Emitting rotate event so annotation can get repositioned.
		if (emitEvent) {
			const event: ImageEditorFlipEvent = { canvasData: this.cropper.getCanvasData(), axis };
			this.$emit("flip", event);
		}
		return this.cropper;
	}

	public complete() {
		this.$emit("cropped", {
			cropBoxData: this.cropper.getCropBoxData(),
			cropperData: this.cropper.getData()
		});
	}

	private update(data: Partial<ImageData>) {
		if (data.url && !this.isDataUrl(data.url)) {
			// If http URL is provided, so we download the image first and convert it to base64 URL to improve the performance of the editor.
			const { url, ...rest } = data;

			Object.assign(this.data, rest);

			this.downloadImageAsBase64Url(this.src, this.mimeType).then((base64Url) => {
				Object.assign(this.data, { url: base64Url });
			});
		} else {
			Object.assign(this.data, data);
		}
	}

	private stop() {
		if (this.cropper) {
			this.cropperData = null;
			this.cropper.destroy();
			this.cropper = undefined as any;
		}
	}

	private clear() {
		this.cropper.clear();
	}

	public restore() {
		if (this.cropper) {
			if (this.disabled) {
				// In order to reset the cropper we need to make it enabled.
				this.cropper.enable();
			}

			// Reseting the cropper changes
			this.cropper.reset();
			this.clear();
			this.emitCanvasChange();

			if (this.disabled) {
				this.cropper.disable();
			}
		}
		this.$emit("restore");
	}
}
